import { Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { solutions, type SolutionData } from "@/data/solutionsData";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { uiT } from "@/i18n/ui";
import { navT } from "@/i18n/nav";

interface ExtraSolutionCard {
  title: string;
  subtitle: string;
  image: string;
  href: string;
}

interface IndustrySolutionsProps {
  /** Filter solutions whose path starts with this prefix */
  industryPath: string;
  /** Additional industry paths to include solutions from */
  additionalPaths?: string[];
  color: string;
  industryName?: string;
  /** Override cross-industry solution paths (defaults to PrecisionDx only) */
  crossIndustrySolutions?: string[];
  /** Extra cards to prepend before solutions (e.g. service pages) */
  extraCards?: ExtraSolutionCard[];
  /** Question-shaped H2 override (GEO) */
  sectionTitle?: string;
}

const DEFAULT_CROSS_INDUSTRY = [
  "/en/industries/companion-diagnostics",
];

const IndustrySolutions = ({ industryPath, additionalPaths = [], color, industryName, crossIndustrySolutions, extraCards = [], sectionTitle }: IndustrySolutionsProps) => {
  const lang = useLanguage();
  const allPaths = [industryPath, ...additionalPaths];
  // Show top-level solutions whose path lives under any of the industry paths
  const relevant = solutions.filter(
    (s) => allPaths.some((p) => s.path.startsWith(p + "/")) && !s.parentSlug,
  );

  const crossPrefixes = crossIndustrySolutions ?? DEFAULT_CROSS_INDUSTRY;

  // Cross-industry solutions (not nested under a specific industry)
  const crossIndustry = solutions.filter(
    (s) => crossPrefixes.some((prefix) => s.path === prefix) && !s.parentSlug,
  );

  if (relevant.length === 0 && crossIndustry.length === 0 && extraCards.length === 0) return null;

  return (
    <section id="industry-solutions" className="py-14 sm:py-20 scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-xs font-bold uppercase tracking-wider mb-2 text-primary">
            {({ en: "Regulatory pathways", es: "Vías regulatorias", "zh-cn": "法规路径" }[lang])}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            {sectionTitle ?? (
              <>
                {navT("Our solutions for", lang)}{lang === "zh-cn" ? "" : " "}
                {industryName || navT("this industry", lang)}
              </>
            )}
          </h2>
          <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
            {navT(
              "Explore purpose-built solutions designed to address the specific regulatory, quality, and compliance challenges in your industry.",
              lang,
            )}
          </p>
        </motion.div>

        {(extraCards.length > 0 || relevant.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-5">
            {extraCards.map((card, i) => (
              <ExtraCard key={card.href} card={card} color={color} index={i} lang={lang} />
            ))}
            {relevant.map((sol, i) => (
              <SolutionCard key={sol.slug} solution={sol} color={color} index={extraCards.length + i} lang={lang} />
            ))}
          </div>
        )}

        {crossIndustry.length > 0 && (
          <>
            <motion.p
              className="text-xs font-bold uppercase tracking-wider mt-12 mb-5 text-muted-foreground"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              {navT("Cross-industry solutions", lang)}
            </motion.p>
            <div className="grid sm:grid-cols-2 gap-5">
              {crossIndustry.map((sol, i) => (
                <SolutionCard key={sol.slug} solution={sol} color={color} index={i} lang={lang} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const SolutionCard = ({
  solution,
  color,
  index,
  lang,
}: {
  solution: SolutionData;
  color: string;
  index: number;
  lang: import("@/hooks/useLanguage").SiteLang;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06, duration: 0.35 }}
    >
      <Link
        to={localizeHref(solution.path, lang)}
        className="group flex flex-col h-full rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg transition-all duration-300"
      >
        {/* Image */}
        <div className="relative h-40 overflow-hidden">
          <img
            src={solution.heroImage}
            alt={solution.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col flex-1">
          <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors mb-1.5 leading-snug">
            {navT(solution.title, lang)}
          </h3>

          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">
            {navT(solution.subtitle, lang)}
          </p>

          <span className="inline-flex items-center gap-1.5 text-xs font-semibold mt-3 transition-colors text-primary">
            {uiT(lang)("learnMore")}
            <ArrowRight
              size={13}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const ExtraCard = ({
  card,
  color,
  index,
  lang,
}: {
  card: ExtraSolutionCard;
  color: string;
  index: number;
  lang: import("@/hooks/useLanguage").SiteLang;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.06, duration: 0.35 }}
  >
    <Link
      to={localizeHref(card.href, lang)}
      className="group flex flex-col h-full rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      <div className="relative h-40 overflow-hidden">
        <img
          src={card.image}
          alt={card.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors mb-1.5 leading-snug">
          {card.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">
          {card.subtitle}
        </p>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold mt-3 transition-colors text-primary">
          {uiT(lang)("learnMore")}
          <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  </motion.div>
);

const CompactSolutionCard = ({
  solution,
  color,
  index,
  lang,
}: {
  solution: SolutionData;
  color: string;
  index: number;
  lang: import("@/hooks/useLanguage").SiteLang;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.06, duration: 0.3 }}
  >
    <Link
      to={localizeHref(solution.path, lang)}
      className="group flex items-center rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-all duration-300"
    >
      <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden">
        <img
          src={solution.heroImage}
          alt={solution.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4 flex-1 min-w-0">
        <h3 className="text-sm font-bold text-foreground group-hover:text-primary transition-colors mb-0.5 leading-snug truncate">
          {solution.title}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          {solution.subtitle}
        </p>
        <span className="inline-flex items-center gap-1 text-xs font-semibold mt-1.5 transition-colors text-primary">
          {uiT(lang)("learnMore")}
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  </motion.div>
);

export default IndustrySolutions;
